import {
  Badge,
  BodyText,
  Button,
  Icon,
  IconSet,
  Textarea,
} from '@postscript/components';
import React, { useState } from 'react';
import styled from 'styled-components';
import { logEvent, ProductArea, PRODUCT_AREAS } from 'utils/events';
import { FeedbackSummary } from '../shopper/components/automations/sampleMessages/types';
import {
  CATEGORY_BANNER_CONFIG,
  DEFAULT_CATEGORY_LABELS,
  FeedbackCategory,
} from './types';

const FeedbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
  padding: var(--spacing-2);
  border: 1px solid var(--border-color-dim);
  border-radius: var(--border-radius-medium);
  overflow: hidden;
  text-align: left;
  margin-top: var(--spacing-2);
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BadgeGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-1);
`;

const CategoryBadge = styled(Badge)`
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
  }

  &[aria-pressed='true'] {
    background-color: var(--gray-9);
    color: var(--white);
  }
`;

const BrandCenterBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  padding: var(--spacing-3);
  background: #f0ebff;
  border: 1px solid #5724e9;
  border-radius: var(--border-radius-medium);
`;

const BannerContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-2);
`;

const BannerIcon = styled.div`
  color: var(--blue-6);
  flex-shrink: 0;
  margin-top: 2px;
`;

const BannerActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-2);
`;

const FeedbackSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
`;

const StyledTextarea = styled(Textarea)`
  font-size: var(--body-text-font-size-x-small);
`;

const SubmitRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-2);
`;

const FeedbackSubmittedLabel = styled.span`
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  font-size: var(--body-text-font-size-x-small);
  color: var(--green-7);
`;

export interface FeedbackPanelProps {
  messageId: string;
  feedback: FeedbackSummary | null;
  onSubmit?: (category: string, feedbackText: string) => void;
  onClose?: () => void;
  title?: string;
  showBrandCenterBanner?: boolean;
  hideBannerCategories?: string[];
  categories?: Record<string, string>;
  productArea?: ProductArea;
  eventName?: string;
}

export const FeedbackPanel: React.FC<FeedbackPanelProps> = ({
  messageId,
  feedback,
  onSubmit,
  onClose,
  title = 'Share Message Feedback',
  showBrandCenterBanner = true,
  hideBannerCategories,
  categories = DEFAULT_CATEGORY_LABELS,
  productArea = PRODUCT_AREAS.SHOPPER,
  eventName = 'shopper sample message feedback submitted',
}) => {
  const category = feedback?.feedbackMetadata?.selectedPills?.[0] || null;
  const feedbackValue = feedback?.feedbackValue || '';
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    category,
  );
  const [feedbackText, setFeedbackText] = useState<string>(feedbackValue);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(
    feedback?.feedbackType || false,
  );
  const [bannerExpanded, setBannerExpanded] = useState(
    feedback?.feedbackType || false,
  );

  const handleCategoryClick = (e: React.MouseEvent, category: string) => {
    e.stopPropagation();
    if (feedbackSubmitted) {
      setBannerExpanded(!bannerExpanded);
      return;
    }
    const newCategory = selectedCategory === category ? null : category;
    setSelectedCategory(newCategory);
  };

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFeedbackText(e.target.value);
  };

  const handleSubmit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedCategory) return;

    logEvent(eventName, {
      product_area: productArea,
      message_id: messageId,
      category: selectedCategory,
      feedback_text: feedbackText,
      feedback_type: 'down',
    });

    setFeedbackSubmitted(true);
    setBannerExpanded(true);
    onSubmit?.(selectedCategory, feedbackText);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose?.();
  };

  const handleGoToRoute = (e: React.MouseEvent) => {
    e.stopPropagation();
    const config = selectedCategory
      ? CATEGORY_BANNER_CONFIG[selectedCategory as FeedbackCategory]
      : null;
    if (!config) return;
    onClose?.();
    window.open(`${window.location.origin}${config.route}`, '_blank');
  };

  const hasCategorySelected = selectedCategory !== null;
  const showInputForm = !feedbackSubmitted;
  const bannerConfig = selectedCategory
    ? CATEGORY_BANNER_CONFIG[selectedCategory as FeedbackCategory]
    : null;

  return (
    <FeedbackContainer onClick={(e) => e.stopPropagation()}>
      {showInputForm ? (
        <>
          <HeaderRow>
            <BodyText size="x-small" color="var(--black)">
              {title}
            </BodyText>
            <Button
              icon={IconSet.Close}
              variant="text"
              size="small"
              onClick={handleClose}
              aria-label="Close panel"
            />
          </HeaderRow>
          <BadgeGroup>
            {Object.entries(categories).map(([key, label]) => (
              <CategoryBadge
                key={key}
                size="small"
                rounded
                variant={selectedCategory === key ? 'info' : 'neutral'}
                onClick={(e: React.MouseEvent) => handleCategoryClick(e, key)}
                aria-pressed={selectedCategory === key}
              >
                {label}
              </CategoryBadge>
            ))}
          </BadgeGroup>

          <FeedbackSection>
            <StyledTextarea
              id="message-feedback-textarea"
              name="message-feedback"
              value={feedbackText}
              onChange={handleFeedbackChange}
              placeholder="Leave feedback for this message"
              message="Feedback is sent to Postscript teams."
              rows={3}
            />
          </FeedbackSection>

          <SubmitRow>
            <Button
              variant="primary"
              size="small"
              onClick={handleSubmit}
              disabled={!hasCategorySelected}
            >
              Submit Feedback
            </Button>
          </SubmitRow>
        </>
      ) : (
        <>
          <HeaderRow>
            <FeedbackSubmittedLabel>
              <Icon
                component={IconSet.CheckCircleFilled}
                size={18}
                color="var(--green-6)"
              />
              Feedback Submitted
            </FeedbackSubmittedLabel>
            <Button
              icon={IconSet.Close}
              variant="text"
              size="small"
              onClick={handleClose}
              aria-label="Close panel"
            />
          </HeaderRow>

          {bannerExpanded &&
            showBrandCenterBanner &&
            bannerConfig &&
            !hideBannerCategories?.includes(selectedCategory ?? '') && (
              <BrandCenterBanner>
                <BannerContent>
                  <BannerIcon>
                    <Icon component={IconSet.Information} size={16} />
                  </BannerIcon>
                  <BodyText size="x-small">{bannerConfig.message}</BodyText>
                </BannerContent>
                <BannerActions>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={handleGoToRoute}
                  >
                    {bannerConfig.buttonLabel}
                  </Button>
                </BannerActions>
              </BrandCenterBanner>
            )}
        </>
      )}
    </FeedbackContainer>
  );
};
